import { Badge, IconButton, Popover } from "@mui/material";
import { useState } from "react";

import { NotificationPanel } from "./NotificationPanel";
import type { MouseEvent } from "react";

import { useUnseenCount } from "@/api/notifications";
import { useIsNotificationsEnabledForSite } from "@/features/Notifications/flag";
import { NotificationsIcon } from "@/icons";

interface NotificationBellProps {
  siteId: string;
}

export function NotificationBell({ siteId }: NotificationBellProps) {
  const flagEnabled = useIsNotificationsEnabledForSite(siteId);
  const { data: unseenCount = 0 } = useUnseenCount(siteId);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const panelOpen = Boolean(anchorEl);

  if (!flagEnabled) return null;

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        data-testid="notification-bell"
        onClick={handleClick}
        size="small"
        aria-label="Notifications"
        aria-haspopup="true"
        aria-expanded={panelOpen ? true : undefined}
        sx={{
          color: "text.primary",
          "&:hover": { backgroundColor: "primary._states.hover" },
        }}
      >
        <Badge
          color="error"
          badgeContent={unseenCount}
          slotProps={{ badge: { "data-testid": "notification-badge" } }}
        >
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Popover
        anchorEl={anchorEl}
        open={panelOpen}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              mt: 3,
              border: 1,
              borderColor: "divider",
              borderRadius: (theme) => `${theme.shape.borderRadiusLg}px`,
              overflow: "hidden",
            },
          },
        }}
      >
        <NotificationPanel siteId={siteId} onNavigate={handleClose} />
      </Popover>
    </>
  );
}
